/**
 * Unified Sentry metadata (web + Chrome extension, same project).
 *
 * | Tag | Web | Extension |
 * | --- | --- | --- |
 * | `sukha.client` | `web` | `chrome_extension` |
 * | `sukha.extension_context` | — | `service_worker` / `popup` / `content_script` |
 * | `stack_contains_chrome_extension` | when mixed stacks | — |
 * | `stack_contains_sukha_extension` | Sukha extension frames in stack | — |
 *
 * Discover examples: `sukha.client:chrome_extension` or `stack_contains_chrome_extension:true`
 * Extension release: `nirvana-chrome-extension@<manifest.version>`
 */
/**
 * Shared Sukha Sentry project DSN (web + Chrome extension).
 *
 * Read from the environment, with **no hardcoded fallback**. There used to be
 * one, and a literal DSN in source is wrong for two reasons that outlive the
 * "is it secret?" question — it is not, it ships in every client bundle:
 *
 *  - it silently defeats the environment. A deployment configured with a
 *    different project, or deliberately with none, still reported to whatever
 *    the constant said, and nothing anywhere disagreed with it.
 *  - it made the environment variable look optional. `NEXT_PUBLIC_SENTRY_DSN`
 *    turned out to be set on all three Vercel targets already, but nobody could
 *    have told from this file, and removing it would have looked harmless.
 *
 * Absent, this is `undefined` and `Sentry.init` disables itself. That is the
 * correct behaviour — it must never be the *unnoticed* behaviour, which is what
 * `warnIfSentryDisabled()` below is for.
 */
export const SUKHA_SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN;

/**
 * Says so, once, when error reporting is off in a deployed environment.
 *
 * "No errors in Sentry" and "Sentry was never switched on" look identical from
 * the outside, and the second one is invisible for exactly as long as it takes
 * to matter. `apps/api/src/sentry.ts` makes the same trade and logs the same
 * way; this is that guard's twin for web.
 */
export function warnIfSentryDisabled(vercelEnv: string | undefined): void {
  if (SUKHA_SENTRY_DSN || !vercelEnv) return;
  /*
   * Runs inside Sentry setup, before any logger exists, and in the browser
   * bundle where Pino is not the transport. Reporting being off is also exactly
   * the condition a logger routed through Sentry could not report.
   */
  // eslint-disable-next-line no-console -- see above
  console.warn(
    "[web] NEXT_PUBLIC_SENTRY_DSN is not set — error reporting is OFF for this deployment"
  );
}

/** Published Chrome Web Store extension id — used to tag mixed page/extension stacks. */
export { CHROME_EXTENSION_ID as SUKHA_CHROME_EXTENSION_ID } from "@/constants/chromeExtensionId";

export const SUKHA_SENTRY_CLIENT_WEB = "web" as const;
export const SUKHA_SENTRY_CLIENT_CHROME_EXTENSION = "chrome_extension" as const;

/**
 * The deployment this event came from — `production`, `preview` or `development`.
 *
 * Without it the SDK falls back to `NODE_ENV`, which Vercel sets to `production`
 * on **preview** builds too. Every preview event therefore arrived tagged
 * `production` and was indistinguishable from a real one in the feed, which is
 * the opposite of what an environment tag is for.
 *
 * `VERCEL_ENV` carries the real answer (`production` | `preview` | `development`)
 * and is the same source `apps/api` reads. The browser cannot see it — only
 * `NEXT_PUBLIC_`-prefixed values are inlined into the client bundle — so the
 * client passes the public twin, which Vercel populates automatically.
 *
 * @param vercelEnv - `VERCEL_ENV` on the server, `NEXT_PUBLIC_VERCEL_ENV` in the browser
 */
export function sukhaSentryEnvironment(vercelEnv: string | undefined): string {
  return vercelEnv || process.env.NODE_ENV || "unknown";
}
